import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Folder as FolderIcon, Archive, Pin, Bookmark, Plus, Trash2, X, Hash } from 'lucide-react';
import { foldersApi, type Folder } from '@/api/folders';
import { useChatStore } from '@/store/chatStore';

type Tab = 'folders' | 'archive' | 'pinned' | 'saved';

export default function LibraryPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('folders');

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-surface-950">
      <div className="h-12 border-b border-surface-800 px-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={() => navigate('/')} className="btn-ghost p-1.5 text-surface-400">
          <ArrowLeft size={18} />
        </button>
        <span className="font-semibold text-surface-200">Библиотека</span>
      </div>

      <div className="flex border-b border-surface-800 flex-shrink-0">
        {([
          ['folders', FolderIcon, 'Папки'],
          ['archive', Archive, 'Архив'],
          ['pinned', Pin, 'Закреплённые'],
          ['saved', Bookmark, 'Сохранённые'],
        ] as Array<[Tab, typeof FolderIcon, string]>).map(([key, Icon, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-1 py-2 text-xs font-medium flex items-center justify-center gap-1 ${
              tab === key ? 'text-brand-400 border-b-2 border-brand-400' : 'text-surface-500 hover:text-surface-300'
            }`}
          >
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'folders' && <FoldersTab />}
        {tab === 'archive' && <ArchiveTab />}
        {tab === 'pinned' && <PinnedChatsTab />}
        {tab === 'saved' && <SavedTab />}
      </div>
    </div>
  );
}

function FoldersTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { joinedChannels } = useChatStore();
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<string | null>(null);

  const { data: folders, isLoading } = useQuery({
    queryKey: ['folders'],
    queryFn: () => foldersApi.list(),
  });

  const createMut = useMutation({
    mutationFn: (name: string) => foldersApi.create({ name }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['folders'] }); setNewName(''); },
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => foldersApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders'] }),
  });
  const removeItemMut = useMutation({
    mutationFn: (vars: { folderId: string; channelId?: string; peerUserId?: string }) =>
      foldersApi.removeItem(vars.folderId, { channelId: vars.channelId, peerUserId: vars.peerUserId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders'] }),
  });
  const addItemMut = useMutation({
    mutationFn: (vars: { folderId: string; channelId: string }) =>
      foldersApi.addItem(vars.folderId, { channelId: vars.channelId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders'] }),
  });

  return (
    <div className="space-y-4 max-w-xl">
      <div className="flex gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) createMut.mutate(newName.trim()); }}
          placeholder="Новая папка…"
          className="irc-input flex-1"
        />
        <button
          onClick={() => newName.trim() && createMut.mutate(newName.trim())}
          disabled={!newName.trim() || createMut.isPending}
          className="btn-primary"
        >
          <Plus size={14} />
        </button>
      </div>

      {isLoading && <p className="text-surface-500 text-sm">Загрузка…</p>}

      {folders?.length === 0 && (
        <p className="text-surface-500 text-sm text-center py-8">Папок пока нет</p>
      )}

      {folders?.map((f: Folder) => (
        <div key={f.id} className="bg-surface-900 border border-surface-800 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <FolderIcon size={14} className="text-brand-400" />
            <span className="text-surface-100 font-medium text-sm flex-1">{f.name}</span>
            <button
              onClick={() => setEditing(editing === f.id ? null : f.id)}
              className="text-xs text-brand-400 hover:underline"
            >
              {editing === f.id ? 'Готово' : 'Изменить'}
            </button>
            <button
              onClick={() => { if (confirm(`Удалить папку "${f.name}"?`)) deleteMut.mutate(f.id); }}
              className="text-surface-500 hover:text-red-400"
            >
              <Trash2 size={13} />
            </button>
          </div>

          <div className="space-y-1">
            {f.items.length === 0 && <p className="text-xs text-surface-600">Пусто</p>}
            {f.items.map((it) => {
              const ch = joinedChannels.find((c) => c.id === it.channelId);
              return (
                <div key={it.id} className="flex items-center gap-2 text-sm">
                  <Hash size={12} className="text-surface-500" />
                  {it.channelId ? (
                    <button
                      onClick={() => navigate(`/channel/${it.channelId}`)}
                      className="flex-1 text-left text-surface-300 hover:text-brand-400 truncate"
                    >
                      {ch?.displayName || ch?.name || it.channelId}
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(`/dm/${it.peerUserId}`)}
                      className="flex-1 text-left text-surface-300 hover:text-brand-400 truncate"
                    >
                      DM
                    </button>
                  )}
                  {editing === f.id && (
                    <button
                      onClick={() => removeItemMut.mutate({
                        folderId: f.id,
                        channelId: it.channelId ?? undefined,
                        peerUserId: it.peerUserId ?? undefined,
                      })}
                      className="text-surface-500 hover:text-red-400"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {editing === f.id && joinedChannels.length > 0 && (
            <div className="mt-2 pt-2 border-t border-surface-800">
              <p className="text-[10px] uppercase text-surface-600 mb-1">Добавить канал</p>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    addItemMut.mutate({ folderId: f.id, channelId: e.target.value });
                    e.currentTarget.value = '';
                  }
                }}
                className="irc-input w-full text-sm"
                defaultValue=""
              >
                <option value="" disabled>— выбрать —</option>
                {joinedChannels
                  .filter((c) => !f.items.some((i) => i.channelId === c.id))
                  .map((c) => (
                    <option key={c.id} value={c.id}>{c.displayName || c.name}</option>
                  ))}
              </select>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ArchiveTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { joinedChannels } = useChatStore();
  const { data, isLoading } = useQuery({
    queryKey: ['archive'],
    queryFn: () => foldersApi.listArchive(),
  });
  const unarchiveMut = useMutation({
    mutationFn: (vars: { channelId?: string; peerUserId?: string }) => foldersApi.unarchive(vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['archive'] }),
  });

  if (isLoading) return <p className="text-surface-500 text-sm">Загрузка…</p>;
  if (!data || data.length === 0) {
    return <p className="text-surface-500 text-sm text-center py-8">Архив пуст</p>;
  }

  return (
    <div className="space-y-2 max-w-xl">
      {data.map((a) => {
        const ch = joinedChannels.find((c) => c.id === a.channelId);
        return (
          <div key={a.id} className="bg-surface-900 border border-surface-800 rounded-lg p-3 flex items-center gap-2">
            <Archive size={14} className="text-surface-500" />
            <button
              onClick={() => navigate(a.channelId ? `/channel/${a.channelId}` : `/dm/${a.peerUserId}`)}
              className="flex-1 text-left text-surface-200 text-sm hover:text-brand-400 truncate"
            >
              {ch?.displayName || ch?.name || (a.channelId ?? a.peerUserId)}
            </button>
            <button
              onClick={() => unarchiveMut.mutate({
                channelId: a.channelId ?? undefined,
                peerUserId: a.peerUserId ?? undefined,
              })}
              className="text-xs text-brand-400 hover:underline"
            >
              Вернуть
            </button>
          </div>
        );
      })}
    </div>
  );
}

function PinnedChatsTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { joinedChannels } = useChatStore();
  const { data, isLoading } = useQuery({
    queryKey: ['pinned-chats'],
    queryFn: () => foldersApi.listPinned(),
  });
  const unpinMut = useMutation({
    mutationFn: (vars: { channelId?: string; peerUserId?: string }) => foldersApi.unpinChat(vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pinned-chats'] }),
  });

  if (isLoading) return <p className="text-surface-500 text-sm">Загрузка…</p>;
  if (!data || data.length === 0) {
    return <p className="text-surface-500 text-sm text-center py-8">Нет закреплённых чатов</p>;
  }

  return (
    <div className="space-y-2 max-w-xl">
      {data.map((p) => {
        const ch = joinedChannels.find((c) => c.id === p.channelId);
        return (
          <div key={p.id} className="bg-surface-900 border border-surface-800 rounded-lg p-3 flex items-center gap-2">
            <Pin size={14} className="text-brand-400" />
            <button
              onClick={() => navigate(p.channelId ? `/channel/${p.channelId}` : `/dm/${p.peerUserId}`)}
              className="flex-1 text-left text-surface-200 text-sm hover:text-brand-400 truncate"
            >
              {ch?.displayName || ch?.name || (p.channelId ?? p.peerUserId)}
            </button>
            <button
              onClick={() => unpinMut.mutate({
                channelId: p.channelId ?? undefined,
                peerUserId: p.peerUserId ?? undefined,
              })}
              className="text-xs text-surface-500 hover:text-red-400"
            >
              Открепить
            </button>
          </div>
        );
      })}
    </div>
  );
}

function SavedTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['saved'],
    queryFn: () => foldersApi.listSaved(),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => foldersApi.deleteSaved(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['saved'] }),
  });

  if (isLoading) return <p className="text-surface-500 text-sm">Загрузка…</p>;
  if (!data || data.length === 0) {
    return <p className="text-surface-500 text-sm text-center py-8">Нет сохранённых сообщений</p>;
  }

  return (
    <div className="space-y-2 max-w-xl">
      {data.map((s) => (
        <div key={s.id} className="bg-surface-900 border border-surface-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Bookmark size={14} className="text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              {s.note && <p className="text-surface-200 text-sm">{s.note}</p>}
              {!s.note && <p className="text-surface-500 text-xs italic">Закладка на сообщение {s.messageId ?? s.dmId}</p>}
              <p className="text-[10px] text-surface-600 mt-1">{new Date(s.createdAt).toLocaleString('ru-RU')}</p>
              {s.tags && s.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {s.tags.map((t) => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-surface-800 text-surface-400">#{t}</span>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => deleteMut.mutate(s.id)}
              className="text-surface-500 hover:text-red-400"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
